/**
 * Enriched fallback rendered by Sentry.ErrorBoundary on uncaught errors.
 *
 * Categories (#375):
 *   1) "We couldn't reach our servers" — network failure, surfaced by per-page
 *      error handling (does not reach the boundary; included for completeness)
 *   2) "We couldn't save what you entered" — backend rejection, also per-page
 *   3) "Something went wrong on our end" — uncaught client exception, the
 *      default for anything the boundary actually catches
 *
 * The request id is pulled from `error.requestId` if the failed request was
 * an API call that attached it (none today), otherwise we generate a UUID
 * client-side so support can correlate to the Sentry event.
 */
import React from 'react';

export type ErrorCategory = 'network' | 'save' | 'client';

const CATEGORY_COPY: Record<ErrorCategory, { title: string; body: string }> = {
  network: {
    title: "We couldn't reach our servers",
    body: 'Check your connection and try again. Your work is saved locally.',
  },
  save: {
    title: "We couldn't save what you entered",
    body: "Some fields may need attention. Try again, or go home if you're stuck.",
  },
  client: {
    title: 'Something went wrong on our end',
    body: "We've been notified. Try again, or head home if the problem persists.",
  },
};

const generateRequestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older environments — non-cryptographic, support-id only.
  return 'rid-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);
};

export interface ErrorFallbackProps {
  /** Error caught by the boundary (or thrown by retry handler). */
  error?: unknown;
  /** Reset the boundary state and re-mount children. */
  resetError?: () => void;
  /** Sentry event id, if available. Surfaced alongside the request id. */
  eventId?: string | null;
  /** Override the category. Defaults to 'client' (uncaught render error). */
  category?: ErrorCategory;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetError,
  eventId,
  category = 'client',
}) => {
  const { title, body } = CATEGORY_COPY[category];

  // Prefer a request id attached to the error by the API client; fall back to
  // a freshly generated UUID so the user always has something to share.
  const errorObj = error as { requestId?: string } | undefined;
  const [requestId] = React.useState(
    () => errorObj?.requestId ?? generateRequestId(),
  );

  const handleRetry = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    window.location.assign('/');
  };

  return (
    <div role="alert" className="error-fallback">
      <h1>{title}</h1>
      <p>{body}</p>
      <p className="error-fallback-id">
        Reference id: <code>{requestId}</code>
        {eventId ? <> (event {eventId})</> : null}
      </p>
      <div className="error-fallback-actions">
        <button type="button" className="btn-primary" onClick={handleRetry}>
          Try again
        </button>
        <button type="button" className="btn-ghost" onClick={handleGoHome}>
          Go home
        </button>
      </div>
    </div>
  );
};

export default ErrorFallback;
